<template>
  <div class="relative w-full flex-1 flex flex-col items-center justify-between">
    <ClientOnly>
      <button
        class="absolute right-4 sm:right-24 top-4 w-[18px] h-[18px] u-center rounded-full bg-[#B2C0FF] text-[#040C47] text-md font-bold"
        @click="showHelpModal()"
      >
        i
      </button>
      <PreviousGamesCard v-if="!isMobile && screenWidth" class="absolute right-0 top-1/4" game-name="bilpop" :start-date="startDate" />
      <OButton
        v-else
        pure
        class="absolute left-4 top-5 w-9 h-9 u-center rounded leaderboardButtonBackground"
        :href="`${isWebview ? '/mobile' : ''}/oyun/leaderboard/bilpop`"
      >
        <OIcon icon="mobile-leaderboard-icon" svg-class="text-[#000746]" />
      </OButton>
      <LeaderboardCard v-if="!isMobile && screenWidth" ref="leaderboard" class="absolute left-0 top-1/4" game-name="bilpop" />
      <OButton
        v-else
        pure
        class="absolute left-4 top-16 w-9 h-9 u-center rounded bg-[#F0E2FF] text-black"
        :href="`${isWebview ? '/mobile' : ''}/oyun/skor/bilpop?month=${date.month()}&year=${date.year()}`"
      >
        <OIcon icon="recent" svg-class="text-[#000746]" />
      </OButton>
    </ClientOnly>

    <!-- logo -->
    <div class="u-center mt-4">
      <OImage loading="lazy" src="/images/bilpop.png" class="emojile-logo w-[162px] h-[55px] sm:w-[188px] sm:h-[65px]" />
      <p class="u-center ml-2 sm:ml-[10px] text-[#F0E2FF] text-[17px] sm:text-22 font-bold leading-24 sm:leading-30">#{{ paddedNumber }}</p>
    </div>
    <div v-if="question" :class="isMobile && 'w-full'">
      <!-- question -->
      <div class="question font-bold text-20 mt-6 text-[#F0E2FF] text-center">100 Kişiye Sorduk</div>
      <div class="question font-semibold text-xl text-[#FF9513] mt-4 mb-6 text-center mx-8 sm:mx-0">{{ question }}</div>
      <!-- answers -->
      <div class="space-y-3 mx-auto w-full sm:w-[600px]">
        <div v-for="(item, index) in gameData" :key="index">
          <div :class="{ circle: checkList[index] }" class="answer flex items-center justify-between h-8 space-x-3">
            <div class="bg-[#44097C] text-[#F0E2FF] u-center h-full w-10 sm:w-12 rounded-tr-full rounded-br-full sm:rounded-full">
              {{ index + 1 }}
            </div>
            <div class="bg-[#44097C] text-[#F0E2FF] u-center flex-grow h-full rounded-full">{{ answerText(item, index) }}</div>
            <div class="bg-[#FF6A34] text-white u-center h-full w-10 sm:w-12 rounded-tl-full rounded-bl-full sm:rounded-full">
              {{ item.score }}
            </div>
          </div>
        </div>
        <!-- rights -->
        <div class="answer flex items-center justify-between h-8 space-x-3">
          <div class="w-10" />
          <div class="bg-[#44097C] text-[#F0E2FF] u-center flex-grow h-full rounded-full">
            <span :class="{ 'text-o-red': limit < 4 }">{{ limit }}</span>
            /10
          </div>
          <div class="w-10" />
        </div>
      </div>
    </div>
    <div class="w-full flex flex-col items-center sm:max-w-[768px]">
      <!-- predictions -->
      <div
        v-if="question && predictions.length > 0"
        class="w-full flex flex-col items-start flex-1 text-[#F8F1FF] px-4 mb-4 text-sm font-medium"
      >
        <div class="my-1.5">Önceki tahminlerin</div>
        <div class="flex flex-wrap">
          <span v-for="item in predictions" :key="item" class="u-center h-5 px-2 mb-2 mr-2 bg-[#44097C] rounded-full">
            {{ item }}
          </span>
        </div>
      </div>
      <!-- answer form -->
      <form
        v-if="question"
        class="answer-form bg-white w-full px-4 sm:px-6 pt-4 sm:pt-6 pb-6 sm:mb-6 flex items-center justify-between sm:rounded-full"
        @submit.prevent="handleSubmitPrediction"
      >
        <div class="relative w-full h-[42px] border border-[#ECD8FF] rounded-full u-center">
          <input
            ref="answerElement"
            v-model="answer"
            type="text"
            :disabled="limit === 0 || isAllTrue()"
            :placeholder="limit === 0 || isAllTrue() ? 'oyun bitti' : 'tahmininiz'"
            class="answer w-full pl-4 h-[40px] pr-20 focus:outline-none rounded-full font-medium text-md text-[#44097C]"
          />
          <button
            v-if="limit > 0 && !isAllTrue()"
            class="
              absolute
              right-[1px]
              top-[1px]
              bottom-0
              w-24
              h-[38px]
              u-center
              text-sm text-white
              bg-[#FF6A34]
              rounded-full
              focus:outline-none
            "
          >
            GÖNDER
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, onMounted, ref, useContext, useAsync, ssrRef, useRoute } from '@nuxtjs/composition-api';
import { useWindowSize } from '@vueuse/core';
import { useBilpopMeta } from '../utils/bilpopMeta';
import { useModal, useToast } from '~/composables/useModal';
import { useTracking } from '~/modules/tracking/composables/useTracking';
import { localStore } from '~/modules/common/utils/localStorageUtils';
import { useAuth } from '~/modules/auth/state/useAuth';
import { useDevice } from '~/composables/useDevice';
import PreviousGamesCard from '~/modules/oyun/components/previousGames/PreviousGamesCard.vue';
import LeaderboardCard from '~/modules/oyun/components/leaderboard/LeaderboardCard.vue';
import { OButton, OImage, OIcon } from '~/modules/common/components/ui';
import { dayjs } from '~/modules/common/utils/dateUtils';

export default defineComponent({
  components: { PreviousGamesCard, LeaderboardCard, OButton, OImage, OIcon },
  head() {},
  props: {
    isWebview: {
      type: Boolean,
      required: false,
    },
  },
  setup(props) {
    const route = useRoute();
    const { user, isLoggedIn } = useAuth();
    const { openModal } = useModal();
    const { toastDanger } = useToast();
    const { $cache, $service } = useContext();
    const { isMobile } = useDevice();
    $cache('public, max-age=30');
    const id = ssrRef(null); // game id
    const answerElement = ref(null);
    const question = ssrRef('');
    const gameData = ssrRef([]);
    const gameNumber = ssrRef(0); // game number
    const nextQuestionTS = ssrRef(0);
    const relatedItems = ssrRef([]); // editor articles
    const spinning = ref(-1);
    const answer = ref(''); // input value
    const predictions = ssrRef([]); // user predictions
    const limit = computed(() => 10 - predictions.value.length);
    const checkList = ssrRef([false, false, false, false, false]);
    const gameStatus = ssrRef('');
    const score = ssrRef(0);
    const startDate = ssrRef(0);
    const date = dayjs(startDate.value);
    const leaderboard = ref(null);

    const validateInput = () => {
      if (answer.value.trim() === '') {
        toastDanger('Tahmin boş olamaz.');
        return false;
      }
      if (predictions.value.length === 10) {
        gameStatus.value = 'LOST';
        toastDanger('Tahmin hakkınız kalmadı.');
        return false;
      }
      if (predictions.value.includes(answer.value) || predictions.value.includes(answer.value.toLocaleLowerCase('tr-TR'))) {
        toastDanger('Daha önce bu cevabı girdiniz.');
        return false;
      }
      return true;
    };

    // answer text for each answer item
    const answerText = (item, index) => {
      if (checkList.value[index] || (checkList.value[index] && spinning.value === index)) {
        return item.answers[0];
      }
      return '?';
    };

    const handleSubmitPrediction = async () => {
      if (!validateInput()) return;
      if (predictions.value.length === 0) {
        window.dataLayer.push({
          event: 'bilpop_actions',
          event_type: 'played',
          event_label: 'success', // success ya da fail gelecek.
          step: '1', // Kaçıncı defa deniyorsa o yazılacak.
        });
      }

      const fp = answer.value.toLocaleLowerCase('tr-TR').trim();
      const index = gameData.value.findIndex((item) => item.answers.find((answer) => answer.toLocaleLowerCase('tr-TR').trim() === fp));
      predictions.value.push(fp);
      answer.value = '';

      // datalayer
      window.dataLayer.push({
        event: 'bilpop_actions',
        event_type: 'played',
        event_label: index < 0 ? 'fail' : 'success', // success ya da fail gelecek.
        step: predictions.value.length, // Kaçıncı defa deniyorsa o yazılacak.
      });

      if (index < 0) {
        handleCalculateScore();
        await setGameData();
        handleResultModal();
        return;
      }

      checkList.value[index] = true;
      spinning.value = index;
      handleCalculateScore();
      setTimeout(async () => {
        spinning.value = -1;
        await setGameData();
        handleResultModal();
      }, 1000);
    };

    // find correct answer index
    const matchedIndex = (prediction) => {
      const fp = prediction.toLocaleLowerCase('tr-TR').trim();
      const index = gameData.value.findIndex((item) => item.answers.find((answer) => answer.toLocaleLowerCase('tr-TR').trim() === fp));
      return index;
    };

    // calculate user score
    const handleCalculateScore = () => {
      let totalScore = 0;
      let consecutiveCorrect = 0;
      for (let pIndex = 0; pIndex < predictions.value.length; pIndex++) {
        const predictionIndex = matchedIndex(predictions.value[pIndex]); // cevap dogru ise indexi !!!!
        if (predictionIndex !== -1) {
          consecutiveCorrect++;
          const nextPrediction = predictions.value[pIndex + 1];
          if (!nextPrediction || matchedIndex(nextPrediction) === -1) {
            let partialTotal = 0;
            for (let index = consecutiveCorrect; index > 0; index--) {
              const tempPredictionIndex = matchedIndex(predictions.value[pIndex - index + 1]);
              if (tempPredictionIndex !== -1) {
                partialTotal += Number(gameData.value[tempPredictionIndex].score);
              }
            }
            totalScore += partialTotal * consecutiveCorrect;
          }
        } else {
          consecutiveCorrect = 0;
        }
      }
      const unusedRights = 10 - predictions.value.length;
      if (isAllTrue()) {
        totalScore += unusedRights * 25;
      }
      score.value = totalScore;
    };

    const isAllTrue = () => checkList.value.every((state) => state === true);

    // show modal
    const handleResultModal = async () => {
      if (isAllTrue()) {
        gameStatus.value = 'WIN';
      } else if (limit.value === 0) {
        gameStatus.value = 'LOST';
      } else {
        return;
      }
      if (!isMobile) await leaderboard.value.getLeaderboard();

      // datalayer
      window.dataLayer.push({
        event: 'bilpop_actions',
        event_type: 'finished',
        event_label: isAllTrue() ? 'success' : 'fail', // oyunun bitişi başarılı ya da başarısız olma durumunda göre success ya da fail olacak.
        step: predictions.value.length, // Kaçıncı defada bittiyse o yazılacak.
      });
      openModal(import('~/modules/bilpop/components/ModalBilpop' /* webpackChunkName: "ModalBilpop" */), {
        gameData: gameData.value,
        relatedItems: relatedItems.value,
        gameNumber: gameNumber.value,
        nextQuestionTS: nextQuestionTS.value,
        score: score.value,
        isToday: !route.value.query.game,
        isWebview: props.isWebview,
      });
    };
    // open how to play modal
    const showHelpModal = () => {
      openModal(import('~/modules/bilpop/components/ModalBilpopHelp' /* webpackChunkName: "ModalBilpopHelp" */));
      // setTimeout(() => {
      //   const elem = document.getElementById('modal');
      //   if (elem) {
      //     elem.style.transform = 'translateY(0)';
      //   }
      // }, 150);
    };
    // show how to play modal
    const howToPlay = () => {
      const data = localStore.getItem('o-blpp-htp');
      if (data) return;
      localStore.setItem('o-blpp-htp', true);
      showHelpModal();
    };

    // keyboardListener
    const keyboardListener = () => {
      document.addEventListener('keyup', (e) => {
        const pressedKey = String(e.key);
        if (pressedKey === 'Tab') {
          answerElement.value.focus();
        }
      });
    };

    const { width } = useWindowSize();
    const screenWidth = computed(() => width.value > 1280);

    const setGameData = async () => {
      const data = {
        id: id.value,
        gameNumber: gameNumber.value,
        predictions: predictions.value,
        checkList: checkList.value,
        score: score.value,
        gameStatus: isAllTrue() || limit.value === 0 ? 'done' : 'in progress',
      };
      if (isLoggedIn.value) {
        const params = {
          gameType: 'bilpop',
          gameId: id.value,
          gameStatus: isAllTrue() || limit.value === 0 ? 'done' : 'in progress',
          score: score.value,
          statusData: data,
        };
        await $service.game.addPlayerStatus(params);
      } else {
        const LSGameData = localStore.getItem('o-bilpop') || {};
        LSGameData[id.value] = data;
        localStore.setItem('o-bilpop', LSGameData);
      }
    };

    const getGameData = () => {
      if (!isLoggedIn.value) {
        const data = localStore.getItem('o-bilpop')?.[id.value];
        if (!data) return;
        id.value = data.id;
        predictions.value = data.predictions;
        checkList.value = data.checkList;
        gameStatus.value = data.gameStatus;
        gameNumber.value = data.gameNumber;
        handleCalculateScore();
      }
      handleResultModal();
    };

    const getUserGameData = (data) => {
      if (!isLoggedIn.value) return;
      if (!data) return;
      id.value = data.id;
      predictions.value = data.predictions;
      checkList.value = data.checkList;
      gameStatus.value = data.gameStatus;
      gameNumber.value = data.gameNumber;
      handleCalculateScore();
    };

    onMounted(() => {
      keyboardListener();
      getGameData();
      howToPlay();
    });
    const paddedNumber = computed(() => {
      return String(gameNumber.value).padStart(4, '0');
    });
    useAsync(async () => {
      if (process.server) {
        try {
          let result = null;
          const userParams = isLoggedIn.value ? { withStatuses: true, playerId: user.value._id } : {};
          if (route.value.query.game) {
            result = (await $service.game.getGameById({ gameType: 'bilpop', gameId: route.value.query.game, ...userParams })).data;
          } else {
            result = (await $service.game.getDailyQuestion({ gameType: 'bilpop', ...userParams })).data;
          }
          id.value = result?.id;
          question.value = result?.question;
          gameData.value = result?.answers;
          gameNumber.value = result?.gameNumber;
          relatedItems.value = result?.relatedItems;
          nextQuestionTS.value = result?.questionExpirationTimestamp;
          startDate.value = result?.startDate;
          getUserGameData(result.playerStatus?.statusData);
        } catch (error) {
          console.log('bilpop err: ', error);
        }
      }
    });

    useBilpopMeta();
    // ------- Google Pageview Event Beginning -------
    const { sendPageviewEvent } = useTracking();
    sendPageviewEvent({
      page: {
        name: `Bilpop | Onedio Oyun`,
        type: 'Onedio Oyun',
      },
    });
    // ------- Google Pageview Event End -------

    return {
      question,
      showHelpModal,
      gameData,
      limit,
      isAllTrue,
      handleSubmitPrediction,
      predictions,
      answerText,
      answerElement,
      answer,
      checkList,
      spinning,
      isMobile,
      paddedNumber,
      startDate,
      date,
      leaderboard,
      screenWidth,
    };
  },
});
</script>

<style lang="scss">
.box {
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.05);
}
.circle {
  -webkit-animation: rotateY 1s linear;
  animation: rotateY 1s linear;
}
@-webkit-keyframes rotateY {
  to {
    -webkit-transform: rotateX(360deg);
  }
}
@keyframes rotateY {
  to {
    transform: rotateX(360deg);
  }
}
@media (max-width: 375px) {
  .box {
    height: 40px;
    font-size: 16px;
  }
  .bilpop-logo {
    display: none;
  }
  .question {
    font-size: 14px;
    margin: 8px 8px;
  }
  .answer {
    height: 28px;
  }
  .answer-form {
    padding: 4px;
  }
}
.leaderboardButtonBackground {
  background: linear-gradient(39deg, #ffba08 5.42%, #fec010 37.01%, #fae63c 103.46%);
}
</style>
